import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceApi from '../utils/attendanceApi';

/* Attendance — full native EOMP app reproducing the standalone app's screens:
   Overview, Attendance (daily/person/week/month), Person detail, People (card
   handover, schedule, archive), Leave, Schedules, Groups, Settings (mailbox/email/
   schedule/holidays), Access, Activity, Import. Light EOMP theme. */

const t = {
  bg: 'var(--bg)', panel: 'var(--surface)', border: 'var(--border)',
  brand: '#7C3AED', text: 'var(--text)', muted: 'var(--muted)',
  green: '#16A34A', amber: '#D97706', red: '#DC2626', blue: '#2563EB', violet: '#7C3AED',
};
const panel = { background: t.panel, border: `1px solid ${t.border}`, borderRadius: 10 };
const inputCss = { width: '100%', background: t.panel, border: `1px solid ${t.border}`, color: t.text, borderRadius: 6, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' };
const labelCss = { fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 5, display: 'block' };
const btn = { background: t.brand, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 15px', fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const ghost = { background: 'none', color: t.muted, border: `1px solid ${t.border}`, borderRadius: 6, padding: '8px 13px', fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const th = { padding: '10px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px' };
const td = { padding: '9px 8px' };

function statusColor(s) {
  if (/present/i.test(s)) return t.green;
  if (/late/i.test(s)) return t.amber;
  if (/absent/i.test(s)) return t.red;
  if (/leave/i.test(s)) return t.violet;
  if (/early|no checkout/i.test(s)) return t.blue;
  return t.muted;
}
function hm(mins) { if (!mins) return '—'; const h = Math.floor(mins / 60), m = mins % 60; return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`; }
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

function displayStatus(s) {
  // Only Present/Absent are real statuses. late / early leave / no checkout are
  // attended -> shown as Present; lateness/early-leave appear as tags, not status.
  const v = String(s || '').toLowerCase();
  if (v === 'late' || v === 'no checkout' || v === 'early leave') return 'present';
  return s;
}
function StatusPill({ s }) { const d = displayStatus(s); return <span style={{ fontSize: 11, fontWeight: 700, color: statusColor(d), textTransform: 'capitalize' }}>{d}</span>; }
function Modal({ title, onClose, children, width = 520 }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20 }}>
      <div style={{ ...panel, width, maxWidth: '100%', maxHeight: '88vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>{title}</h3><div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ ...ghost, padding: '4px 10px' }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Field({ label, children }) { return <div style={{ marginBottom: 12 }}><label style={labelCss}>{label}</label>{children}</div>; }

// Floating toast (item 2): every Save/action gives a visible, unmissable confirmation
// that doesn't depend on scroll position.
function Toast({ toast }) {
  if (!toast) return null;
  const c = toast.kind === 'error' ? t.red : toast.kind === 'info' ? t.blue : t.green;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 90,
      background: t.panel, border: `1px solid ${c}`, borderLeft: `4px solid ${c}`,
      borderRadius: 10, padding: '12px 18px', boxShadow: '0 8px 30px rgba(20,26,38,0.25)',
      fontSize: 13.5, fontWeight: 600, color: t.text, display: 'flex', alignItems: 'center', gap: 10,
      minWidth: 240, maxWidth: 480,
    }}>
      <span style={{ color: c, fontSize: 16 }}>{toast.kind === 'error' ? '✕' : '✓'}</span>
      <span>{toast.text}</span>
    </div>
  );
}
function useToast() {
  const [toast, setToast] = useState(null);
  const show = useCallback((text, kind = 'success') => {
    setToast({ text, kind });
    setTimeout(() => setToast(null), 3200);
  }, []);
  return { toast, show };
}

// Sortable columns (item 3). Click a header to sort by it; click again to flip.
function useSort(defaultKey, defaultDir = 'desc') {
  const [key, setKey] = useState(defaultKey);
  const [dir, setDir] = useState(defaultDir);
  const toggle = (k) => {
    if (k === key) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setKey(k); setDir('desc'); }
  };
  const sortRows = (rows, accessors) => {
    const get = (r) => (accessors && accessors[key] ? accessors[key](r) : r[key]);
    return [...rows].sort((a, a2) => {
      let x = get(a), y = get(a2);
      if (x == null) x = ''; if (y == null) y = '';
      if (typeof x === 'string' && typeof y === 'string') {
        const c = x.localeCompare(y); return dir === 'asc' ? c : -c;
      }
      const c = (Number(x) || 0) - (Number(y) || 0); return dir === 'asc' ? c : -c;
    });
  };
  return { key, dir, toggle, sortRows };
}
// Clickable table header cell.
function SortTh({ label, k, sort, style, align }) {
  const active = sort.key === k;
  return (
    <th onClick={() => sort.toggle(k)} style={{ ...th, ...style, cursor: 'pointer', userSelect: 'none', textAlign: align || 'left', whiteSpace: 'nowrap' }}>
      {label}<span style={{ marginLeft: 4, opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

// Full-page modal (item 6): covers almost the whole viewport, for the profile and
// history pop-outs. Same visual language as the rest of the app.
function FullModal({ title, subtitle, onClose, children }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: '3vh 3vw' }}>
      <div style={{ ...panel, width: '94vw', height: '94vh', maxWidth: 1280, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 22px', borderBottom: `1px solid ${t.border}` }}>
          <div><h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h3>{subtitle && <div style={{ fontSize: 12, color: t.muted, marginTop: 2 }}>{subtitle}</div>}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ ...ghost, padding: '6px 12px' }}>✕ Close</button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 22 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Day timeline: 06:00–22:00 track, shift shaded, worked span drawn ──
const TL_FROM = 6 * 60, TL_TO = 22 * 60, TL_SPAN = TL_TO - TL_FROM;
function tlPct(hhmmss) {
  if (!hhmmss) return null;
  const [h, m] = String(hhmmss).split(':').map(Number);
  return ((h * 60 + m - TL_FROM) / TL_SPAN) * 100;
}
const clamp = (v) => Math.max(0, Math.min(100, v));

function DayTrack({ firstIn, lastOut, shiftStart, shiftEnd, status, width = 150 }) {
  const a = tlPct(shiftStart), b = tlPct(shiftEnd);
  const x = tlPct(firstIn), y = tlPct(lastOut);
  const colour = statusColor(displayStatus(status || ''));
  const title = firstIn
    ? `${String(firstIn).slice(0, 5)} → ${lastOut ? String(lastOut).slice(0, 5) : 'no check-out'}`
    : 'no swipe';
  return (
    <div title={title} style={{
      position: 'relative', width, height: 20, background: t.panel,
      border: `1px solid ${t.border}`, borderRadius: 4, overflow: 'hidden',
    }}>
      {[8, 10, 12, 14, 16, 18, 20].map(h => (
        <div key={h} style={{
          position: 'absolute', top: 0, bottom: 0, width: 1, background: t.border, opacity: 0.5,
          left: `${((h * 60 - TL_FROM) / TL_SPAN) * 100}%`,
        }} />
      ))}
      {a !== null && b !== null && b > a && (
        <div style={{
          position: 'absolute', top: 0, bottom: 0, left: `${clamp(a)}%`,
          width: `${Math.max(0, clamp(b) - clamp(a))}%`,
          background: t.border, opacity: 0.35,
        }} />
      )}
      {x !== null && y !== null && (
        <>
          <div style={{
            position: 'absolute', top: 4, bottom: 4, left: `${clamp(x)}%`,
            width: `${Math.max(0.8, clamp(y) - clamp(x))}%`,
            background: colour, borderRadius: 3, opacity: 0.9,
          }} />
          <div style={{ position: 'absolute', top: 1, bottom: 1, left: `${clamp(x)}%`, width: 2, background: t.text }} />
          <div style={{ position: 'absolute', top: 1, bottom: 1, left: `${clamp(y - 0.2)}%`, width: 2, background: t.text }} />
        </>
      )}
      {x !== null && y === null && (
        <div style={{ position: 'absolute', top: 1, bottom: 1, left: `${clamp(x)}%`, width: 2, background: colour }} />
      )}
    </div>
  );
}

function TrackLegend() {
  return (
    <div style={{ fontSize: 11, color: t.muted, padding: '8px 12px' }}>
      Day runs 06:00 to 22:00 · shaded band is the shift · bar is first check-in to last check-out
    </div>
  );
}

// ── Person picker: fixed-size button opening a search + checkbox dialog ──
function PeoplePicker({ people, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [draft, setDraft] = useState(selected);
  useEffect(() => { if (open) { setDraft(selected); setQ(''); } }, [open, selected]);

  const visible = people.filter(p => {
    const hay = `${p.name || p.display_name} ${p.person_id} ${p.employee_no || ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const toggle = (pid) => setDraft(d => d.includes(pid) ? d.filter(v => v !== pid) : [...d, pid]);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        style={{ ...ghost, width: '100%', textAlign: 'left', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span>{selected.length ? `${selected.length} selected` : 'Everyone'}</span>
        <span style={{ color: t.muted }}>▾</span>
      </button>
      {open && (
        <Modal title="Choose people" onClose={() => setOpen(false)} width={480}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search by name, card or employee number…"
            style={{ ...inputCss, marginBottom: 10 }} />
          <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>
            {draft.length ? `${draft.length} selected` : 'Nothing ticked — everyone is included'}
          </div>
          <div style={{ maxHeight: 320, overflow: 'auto', border: `1px solid ${t.border}`, borderRadius: 6 }}>
            {visible.length === 0 && <div style={{ padding: 16, color: t.muted, fontSize: 13 }}>Nobody matches.</div>}
            {visible.map(p => (
              <label key={p.person_id} style={{
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 11px',
                borderBottom: `1px solid ${t.border}`, cursor: 'pointer', fontSize: 13,
              }}>
                <input type="checkbox" checked={draft.includes(p.person_id)} onChange={() => toggle(p.person_id)} />
                <span style={{ flex: 1 }}>{p.name || p.display_name}</span>
                <span style={{ fontSize: 11, color: t.muted }}>{p.employee_no || p.person_id}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
            <button style={ghost} onClick={() => setDraft([])}>Clear all</button>
            <button style={ghost} onClick={() => setOpen(false)}>Cancel</button>
            <button style={btn} onClick={() => { onChange(draft); setOpen(false); }}>Show these people</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Shared filter state ──
const startOfMonth = () => today().slice(0, 8) + '01';
// Shared, persistent attendance filter — survives navigating between pages within
// the session so the same filter stays applied everywhere in the app.
let _attFilterStore = null;
function loadAttFilter() {
  if (_attFilterStore) return _attFilterStore;
  try { const s = sessionStorage.getItem('att_filter'); if (s) { _attFilterStore = JSON.parse(s); return _attFilterStore; } } catch (e) {}
  _attFilterStore = { from: startOfMonth(), to: today(), group_id: '', people: [], status: 'all', rest: 'hide', preset: 'month' };
  return _attFilterStore;
}
function saveAttFilter(f) { _attFilterStore = f; try { sessionStorage.setItem('att_filter', JSON.stringify(f)); } catch (e) {} }

function useAttendanceFilters(initial = {}) {
  const [f, setF] = useState(() => loadAttFilter());
  const set = (patch) => setF(prev => { const next = { ...prev, ...patch }; saveAttFilter(next); return next; });

  const preset = (key) => {
    const map = {
      day: { from: today(), to: today() },
      week: { from: daysAgo(6), to: today() },
      month: { from: startOfMonth(), to: today() },
      prev_month: (() => {
        const d = new Date(); d.setUTCDate(1); d.setUTCDate(0);
        const end = d.toISOString().slice(0, 10);
        return { from: end.slice(0, 8) + '01', to: end };
      })(),
      year: { from: daysAgo(364), to: today() },
    };
    set({ ...map[key], preset: key });
  };

  const qs = new URLSearchParams();
  qs.set('from', f.from); qs.set('to', f.to);
  if (f.group_id) qs.set('group_id', f.group_id);
  if (f.status !== 'all') qs.set('status', f.status);
  if (f.rest !== 'hide') qs.set('rest', f.rest);
  f.people.forEach(p => qs.append('people', p));

  return { f, set, preset, qs: '?' + qs.toString() };
}

// ── Filter bar ──
function FilterBar({ f, set, preset, groups, people, views, view, onView }) {
  const chip = (on) => ({
    ...(on ? btn : ghost), padding: '6px 12px', fontSize: 12,
    borderRadius: 999, fontWeight: 600,
  });
  const days = Math.max(1, Math.round((new Date(f.to) - new Date(f.from)) / 864e5) + 1);

  return (
    <div style={{ ...panel, marginBottom: 16, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: '11px 14px', borderBottom: `1px solid ${t.border}` }}>
        {[['day', 'Latest day'], ['week', 'Last 7 days'], ['month', 'This month'],
          ['prev_month', 'Last month'], ['year', '12 months']].map(([k, l]) => (
          <button key={k} onClick={() => preset(k)} style={chip(f.preset === k)}>{l}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: t.muted }}>
          {f.from} → {f.to} · {days} day{days === 1 ? '' : 's'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', padding: '12px 14px' }}>
        <div style={{ minWidth: 130 }}>
          <label style={labelCss}>From</label>
          <input type="date" value={f.from} style={inputCss}
            onChange={e => set({ from: e.target.value, preset: '' })} />
        </div>
        <div style={{ minWidth: 130 }}>
          <label style={labelCss}>To</label>
          <input type="date" value={f.to} style={inputCss}
            onChange={e => set({ to: e.target.value, preset: '' })} />
        </div>
        <div style={{ minWidth: 150 }}>
          <label style={labelCss}>Group</label>
          <select value={f.group_id} style={inputCss}
            onChange={e => set({ group_id: e.target.value, people: [] })}>
            <option value="">All groups</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            <option value="none">Not in a group</option>
          </select>
        </div>
        <div style={{ minWidth: 170 }}>
          <label style={labelCss}>Person</label>
          <PeoplePicker people={people} selected={f.people} onChange={v => set({ people: v })} />
        </div>
        <div style={{ minWidth: 140 }}>
          <label style={labelCss}>Status</label>
          <select value={f.status} style={inputCss} onChange={e => set({ status: e.target.value })}>
            <option value="all">Any status</option>
            <option value="present">Present</option>
            <option value="absent">Absent</option>
            <optgroup label="Flags">
              <option value="latecomers">Came in late</option>
              <option value="earlyleavers">Left early</option>
              <option value="estimated">Estimated checkout</option>
              <option value="flagged">Any flag</option>
            </optgroup>
          </select>
        </div>
        <div style={{ minWidth: 140 }}>
          <label style={labelCss}>Rest days</label>
          <select value={f.rest} style={inputCss} onChange={e => set({ rest: e.target.value })}>
            <option value="hide">Hidden</option>
            <option value="include">Included</option>
            <option value="only">Only rest days</option>
          </select>
        </div>
      </div>

      {views && (
        <div style={{ display: 'flex', borderTop: `1px solid ${t.border}` }}>
          {views.map(([k, l]) => (
            <button key={k} onClick={() => onView(k)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 16px', fontSize: 12, fontWeight: 700,
              letterSpacing: '.3px', textTransform: 'uppercase',
              color: view === k ? t.brand : t.muted,
              borderBottom: `2px solid ${view === k ? t.brand : 'transparent'}`,
            }}>{l}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Days-in-period strip ──
function DayStrip({ shape }) {
  const colours = {
    held: [t.green, 0.14], gap: [t.red, 0.14],
    holiday: [t.violet, 0.16], rest: [t.muted, 0.10], leave: [t.blue, 0.16],
  };
  const label = { held: 'report held', gap: 'no report', holiday: 'public holiday', rest: 'rest day', leave: 'on leave' };
  // a day showing the selected person's leave is drawn as leave, overriding its base kind
  const kindOf = (d) => d.leave ? 'leave' : d.kind;
  const counts = shape.reduce((a, d) => ({ ...a, [kindOf(d)]: (a[kindOf(d)] || 0) + 1 }), {});
  const anyLeave = shape.some(d => d.leave);
  const anyHoliday = shape.some(d => d.holiday_name || d.kind === 'holiday');
  return (
    <div style={{ ...panel, padding: 16, marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Days in this period</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {shape.map(d => {
          const kind = kindOf(d);
          const [c, o] = colours[kind] || colours.rest;
          const tip = d.leave ? `${d.day} — on leave (${d.leave})`
            : d.holiday_name ? `${d.day} — ${d.holiday_name}`
            : `${d.day} — ${label[kind]}${d.rostered ? `, ${d.rostered} rostered` : ''}`;
          return (
            <div key={d.day} title={tip}
              style={{
                width: 34, height: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
                fontSize: 10, borderRadius: 4, border: `1px solid ${c}`, color: c,
                background: `color-mix(in srgb, ${c} ${o * 100}%, transparent)`,
              }}>
              <span style={{ fontSize: 8.5, opacity: 0.85, fontWeight: 700 }}>{new Date(d.day + 'T00:00:00Z').toLocaleDateString('en', { weekday: 'short', timeZone: 'UTC' })}</span>
              <span style={{ fontWeight: 700 }}>{Number(d.day.slice(8))}</span>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 11, color: t.muted, flexWrap: 'wrap' }}>
        {Object.entries(label).filter(([k]) => (k !== 'leave' || anyLeave) && (k !== 'holiday' || anyHoliday)).map(([k, l]) => (
          <span key={k}><span style={{
            display: 'inline-block', width: 9, height: 9, borderRadius: 2, marginRight: 5,
            background: (colours[k] || colours.rest)[0],
          }} />{l} ({counts[k] || 0})</span>
        ))}
      </div>
    </div>
  );
}


export default function AttendancePage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [view, setView] = useState('overview');
  const [personId, setPersonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    attendanceApi.get('/me').then(m => { setMe(m); setLoading(false); }).catch(e => { setErr(e?.error || 'Failed to load Attendance.'); setLoading(false); });
  }, []);

  const openPerson = (pid) => { setPersonId(pid); setView('person'); };
  // Grouped sidebar (item 7): sections with headers; each entry opens its page.
  const sections = [];
  const reports = [['overview', 'Overview', '▦'], ['attendance', 'Attendance', '▤']];
  if (me?.can_manage_leave) reports.push(['leave', 'Leave', '◷']);
  sections.push({ title: 'Reports', items: reports });
  const peopleItems = [['people', 'People', '👥']];
  sections.push({ title: 'People', items: peopleItems });
  if (me?.is_admin) {
    sections.push({ title: 'Configuration', items: [
      ['schedules', 'Schedules', '⏱'], ['groups', 'Groups', '◲'], ['settings', 'Settings', '⚙'], ['access', 'Access', '🔑'],
    ] });
    sections.push({ title: 'Data', items: [
      ['import', 'Import', '⬆'], ['activity', 'History', '☰'],
    ] });
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: t.bg, color: t.text, display: 'flex', fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ width: 236, background: t.panel, borderRight: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', flex: '0 0 auto' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: t.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800 }}>A</div>
          <div><div style={{ fontSize: 14, fontWeight: 800 }}>Attendance</div><div style={{ fontSize: 9, color: t.muted, textTransform: 'capitalize' }}>{me?.level || '—'}</div></div>
        </div>
        <div style={{ padding: 10, flex: 1, overflow: 'auto' }}>
          {sections.map(sec => (
            <SidebarSection key={sec.title} title={sec.title} items={sec.items} view={view} setView={setView} />
          ))}
        </div>
        <div style={{ padding: 12, borderTop: `1px solid ${t.border}` }}>
          <button onClick={() => navigate('/hub')} style={{ ...ghost, width: '100%' }}>← App Hub</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
        {loading && <div style={{ color: t.muted, padding: 40, textAlign: 'center' }}>Loading…</div>}
        {!loading && err && <div style={{ ...panel, padding: 30, color: t.red, textAlign: 'center' }}>{err}</div>}
        {!loading && !err && view === 'overview' && <OverviewView me={me} openPerson={openPerson} />}
        {!loading && !err && view === 'attendance' && <AttendanceView me={me} openPerson={openPerson} />}
        {!loading && !err && view === 'people' && <PeopleView me={me} openPerson={openPerson} />}
        {!loading && !err && view === 'person' && <PersonView me={me} pid={personId} back={() => setView('people')} />}
        {!loading && !err && view === 'leave' && me?.can_manage_leave && <LeaveView />}
        {!loading && !err && view === 'import' && me?.is_admin && <ImportView />}
        {!loading && !err && view === 'schedules' && me?.is_admin && <SchedulesView />}
        {!loading && !err && view === 'groups' && me?.is_admin && <GroupsView />}
        {!loading && !err && view === 'settings' && me?.is_admin && <SettingsView />}
        {!loading && !err && view === 'access' && me?.is_admin && <AccessView />}
        {!loading && !err && view === 'activity' && me?.is_admin && <ActivityView />}
      </div>
    </div>
  );
}

function SidebarSection({ title, items, view, setView }) {
  const [open, setOpen] = useState(true);
  const active = items.some(([k]) => k === view);
  return (
    <div style={{ marginBottom: 6 }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
        color: t.muted, fontSize: 10.5, fontWeight: 800, letterSpacing: '.6px',
        textTransform: 'uppercase', padding: '8px 10px 4px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ color: active ? t.brand : t.muted }}>{title}</span>
        <span style={{ fontSize: 9, opacity: 0.7 }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && items.map(([k, l, icon]) => (
        <button key={k} onClick={() => setView(k)} style={{
          width: '100%', textAlign: 'left', background: view === k ? t.brand : 'transparent',
          color: view === k ? '#fff' : t.text, border: 'none', borderRadius: 8,
          padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          marginBottom: 2, display: 'flex', alignItems: 'center', gap: 9,
        }}>
          <span style={{ width: 16, fontSize: 12, opacity: view === k ? 1 : 0.6 }}>{icon}</span>{l}
        </button>
      ))}
    </div>
  );
}


function OverviewView({ me, openPerson }) {
  const { f, set, preset, qs } = useAttendanceFilters();
  const [head, setHead] = useState(null);
  const [people, setPeople] = useState([]);
  const [shape, setShape] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pickPeople, setPickPeople] = useState([]);
  const singleDay = f.from === f.to;

  useEffect(() => { attendanceApi.get('/groups').then(setGroups).catch(() => setGroups([])); }, []);
  useEffect(() => {
    const g = f.group_id ? `?group_id=${f.group_id}` : '';
    attendanceApi.get('/people' + g).then(setPickPeople).catch(() => setPickPeople([]));
  }, [f.group_id]);

  const load = useCallback(async () => {
    try {
      const [h, pp, sh] = await Promise.all([
        attendanceApi.get('/headline' + qs),
        attendanceApi.get('/per-person' + qs),
        attendanceApi.get('/day-shape' + qs),
      ]);
      setHead(h); setPeople(pp); setShape(sh);
    } catch (e) { /* empty */ }
  }, [qs]);
  useEffect(() => { load(); }, [load]);

  // Filter-aware summary cards (item 4) — these replace the two charts.
  const totalWorkedH = head ? (head.worked_minutes || 0) / 60 : 0;
  const targetH = people.reduce((a, p) => a + Number(p.target_hours || 0), 0);
  const attendanceRate = head && head.days ? Math.round((head.days - (head.absent_days || 0)) / head.days * 100) : 0;
  const cards = [
    { label: 'People in view', value: head?.people || 0, sub: `${head?.days || 0} person-days`, color: t.brand },
    { label: 'Attendance rate', value: attendanceRate + '%', sub: `${head?.absent_days || 0} absences`, color: attendanceRate >= 90 ? t.green : t.amber },
    { label: 'Hours worked', value: Math.round(totalWorkedH) + 'h', sub: targetH ? `of ${Math.round(targetH)}h target` : 'vs target', color: t.blue },
    { label: 'Late arrivals', value: head?.late_days || 0, sub: 'across the period', color: (head?.late_days ? t.amber : t.muted) },
    { label: 'On leave', value: head?.leave_days || 0, sub: 'approved days', color: t.violet },
  ];

  const sort = useSort('_exc', 'desc');
  const accessors = {
    _exc: p => (p.late_days || 0) + (p.absent_days || 0) + (p.open_days || 0),
    name: p => p.name || '', schedule_name: p => p.schedule_name || '',
    first_in: p => p.first_in || '', last_out: p => p.last_out || '',
    days_due: p => p.days_due || 0, worked_minutes: p => p.worked_minutes || 0,
    target_hours: p => Number(p.target_hours || 0),
    delta: p => (p.worked_minutes || 0) / 60 - Number(p.target_hours || 0),
    late_days: p => p.late_days || 0, absent_days: p => p.absent_days || 0, open_days: p => p.open_days || 0,
  };
  const ranked = sort.sortRows(people, accessors);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Overview</h2>
      </div>
      <FilterBar f={f} set={set} preset={preset} groups={groups} people={pickPeople} />

      {/* professional summary cards, responsive to the filters (item 4) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))', gap: 12, marginBottom: 16 }}>
        {cards.map(c => (
          <div key={c.label} style={{ ...panel, padding: '16px 18px' }}>
            <div style={{ fontSize: 12, color: t.muted, fontWeight: 600, marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: c.color, lineHeight: 1 }}>{c.value}</div>
            <div style={{ fontSize: 11, color: t.muted, marginTop: 5 }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {shape.length > 0 && <DayStrip shape={shape} />}

      {/* one full per-person table (item 3): sortable columns, check-in / check-out
          on single-day views, schedule per person, no status blocks */}
      <div style={{ ...panel, overflow: 'auto' }}>
        <div style={{ padding: '12px 16px', borderBottom: `1px solid ${t.border}`, fontSize: 13, fontWeight: 700 }}>
          People {ranked.length ? `(${ranked.length})` : ''}
          <span style={{ fontWeight: 400, color: t.muted, fontSize: 11, marginLeft: 8 }}>click a column to sort</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>
            <SortTh label="Person" k="name" sort={sort} />
            <SortTh label="Schedule" k="schedule_name" sort={sort} />
            {singleDay
              ? <><SortTh label="First in" k="first_in" sort={sort} /><SortTh label="Last out" k="last_out" sort={sort} /></>
              : <SortTh label="Days due" k="days_due" sort={sort} />}
            <SortTh label="Worked" k="worked_minutes" sort={sort} />
            <SortTh label="Target" k="target_hours" sort={sort} />
            <SortTh label="Δ" k="delta" sort={sort} />
            <SortTh label="Late" k="late_days" sort={sort} />
            <SortTh label="Absent" k="absent_days" sort={sort} />
            <SortTh label="Est." k="open_days" sort={sort} />
          </tr></thead>
          <tbody>
            {ranked.length === 0 && <tr><td colSpan={10} style={{ padding: 22, textAlign: 'center', color: t.muted }}>No data for these filters.</td></tr>}
            {ranked.map(p => {
              const workedH = (p.worked_minutes || 0) / 60, tH = Number(p.target_hours || 0), delta = workedH - tH;
              return (
                <tr key={p.person_id} style={{ borderBottom: `1px solid ${t.border}`, cursor: 'pointer' }} onClick={() => openPerson(p.person_id)}>
                  <td style={{ ...td, fontWeight: 600 }}>{p.name}<div style={{ fontSize: 11, color: t.muted, fontWeight: 400 }}>{p.person_id}</div></td>
                  <td style={{ ...td, color: t.muted, fontSize: 12 }}>{p.schedule_name || 'Default'}</td>
                  {singleDay
                    ? <><td style={td}>{(p.first_in || '—')}</td><td style={td}>{(p.last_out || '—')}</td></>
                    : <td style={{ ...td, color: t.muted }}>{p.days_due}</td>}
                  <td style={td}>{hm(p.worked_minutes)}</td>
                  <td style={{ ...td, color: t.muted }}>{tH ? tH + 'h' : '—'}</td>
                  <td style={{ ...td, color: delta >= 0 ? t.green : t.red, fontWeight: 600 }}>{tH ? (delta >= 0 ? '+' : '') + delta.toFixed(1) + 'h' : '—'}</td>
                  <td style={{ ...td, color: p.late_days ? t.amber : t.muted }}>{p.late_days}</td>
                  <td style={{ ...td, color: p.absent_days ? t.red : t.muted }}>{p.absent_days}</td>
                  <td style={{ ...td, color: p.open_days ? t.blue : t.muted }}>{p.open_days || 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function AttendanceView({ me, openPerson }) {
  const { f, set, preset, qs } = useAttendanceFilters();
  const [tab, setTab] = useState('daily');
  const sort = useSort('worked_minutes', 'desc');
  const [records, setRecords] = useState(null);
  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    attendanceApi.get('/groups').then(setGroups).catch(() => setGroups([]));
  }, []);
  useEffect(() => {
    const g = f.group_id ? `?group_id=${f.group_id}` : '';
    attendanceApi.get('/people' + g).then(setPeople).catch(() => setPeople([]));
  }, [f.group_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'daily') setRows(await attendanceApi.get('/attendance' + qs));
      else if (tab === 'person') setRows(await attendanceApi.get('/per-person' + qs));
      else setRows(await attendanceApi.get('/by-period' + qs + '&bucket=' + tab));
    } catch (e) { setRows([]); } finally { setLoading(false); }
  }, [qs, tab]);
  useEffect(() => { load(); }, [load]);

  const excel = async () => {
    try {
      const token = localStorage.getItem('flowup_token');
      const resp = await fetch('/api/attendance/export.xlsx' + qs, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include',
      });
      if (!resp.ok) throw new Error();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(await resp.blob());
      a.download = `attendance_${f.from}_${f.to}.xlsx`; a.click();
    } catch (e) { alert('Excel export failed.'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, gap: 10 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Attendance</h2>
        <div style={{ flex: 1 }} />
        {me?.can_export && <button style={btn} onClick={excel}>⬇ Export to Excel</button>}
      </div>

      <FilterBar f={f} set={set} preset={preset} groups={groups} people={people}
        view={tab} onView={setTab}
        views={[['daily', 'Every day'], ['person', 'Per person'],
                ['week', 'Per week'], ['month', 'Per month']]} />

      <div style={{ ...panel, overflow: 'auto' }}>
        {loading && <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>Loading…</div>}
        {!loading && rows.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: t.muted, fontSize: 13 }}>
            Nothing matches these filters.
          </div>
        )}

        {!loading && rows.length > 0 && tab === 'daily' && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>
                <SortTh label="Date" k="work_date" sort={sort} />
                <SortTh label="Person" k="display_name" sort={sort} />
                <SortTh label="Schedule" k="schedule_name" sort={sort} />
                <SortTh label="First check-in" k="first_in" sort={sort} />
                <SortTh label="Last check-out" k="last_out" sort={sort} />
                <SortTh label="Hours" k="worked_minutes" sort={sort} />
                <SortTh label="Actual worked" k="net_minutes" sort={sort} />
                <th style={th}>Due</th>
                <SortTh label="Late" k="late_minutes" sort={sort} />
                <th style={th}>Day</th>
                <SortTh label="Status" k="status" sort={sort} />
                <th style={th}>Reason</th>
              </tr></thead>
              <tbody>
                {sort.sortRows(rows, { display_name: r => r.display_name || '' }).map(r => (
                  <tr key={r.person_id + r.work_date} style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={td}>{r.work_date}</td>
                    <td style={{ ...td, fontWeight: 600, cursor: 'pointer' }} onClick={() => openPerson(r.person_id)}>
                      {r.display_name}
                      <div style={{ fontSize: 11, color: t.muted, fontWeight: 400 }}>{r.person_id}</div>
                    </td>
                    <td style={{ ...td, color: t.muted, fontSize: 12 }}>{r.schedule_name}</td>
                    <td style={td}>{(r.first_in || '—').slice(0, 5)}</td>
                    <td style={td}>{(r.last_out || '—').slice(0, 5)}</td>
                    <td style={td}>{hm(r.worked_minutes)}{(r.flags && (Array.isArray(r.flags) ? r.flags : (() => { try { return JSON.parse(r.flags); } catch (e) { return []; } })()).some(f => /assumed/i.test(f))) && <span title="Checkout was estimated at shift end" style={{ fontSize: 9, color: t.amber, marginLeft: 4, fontWeight: 700 }}>est</span>}</td>
                    <td style={{ ...td, cursor: (r.segments && r.segments.length) ? 'pointer' : 'default', color: t.brand, fontWeight: 600 }} title="Click to see the in/out records" onClick={() => { if (r.segments && r.segments.length) setRecords({ name: r.display_name, date: r.work_date, segments: r.segments, gaps: r.gaps, net: r.net_minutes }); }}>{hm(r.net_minutes)}{(r.segments && r.segments.length > 1) && <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.7 }}>▦</span>}</td>
                    <td style={{ ...td, color: t.muted }}>{Math.round(r.daily_hours || 8)}h</td>
                    <td style={{ ...td, color: r.late_minutes ? t.amber : t.muted }}>{hm(r.late_minutes)}</td>
                    <td style={td}>
                      <DayTrack firstIn={r.first_in} lastOut={r.last_out}
                        shiftStart={r.shift_start} shiftEnd={r.shift_end} status={r.status} width={100} />
                    </td>
                    <td style={td}><StatusPill s={r.status} />{r.late_minutes > 0 && <span title={`Arrived ${hm(r.late_minutes)} late`} style={{ fontSize: 9, color: '#fff', background: t.amber, borderRadius: 4, padding: '1px 5px', marginLeft: 6, fontWeight: 700 }}>LATE</span>}{r.early_minutes > 0 && <span title={`Left ${hm(r.early_minutes)} early`} style={{ fontSize: 9, color: '#fff', background: t.blue, borderRadius: 4, padding: '1px 5px', marginLeft: 4, fontWeight: 700 }}>EARLY</span>}</td>
                    <td style={{ ...td, fontSize: 12, color: t.muted }}>{r.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TrackLegend />
          </>
        )}

        {!loading && rows.length > 0 && tab !== 'daily' && (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>
                {tab !== 'person' && <SortTh label="Period" k="period" sort={sort} />}
                <SortTh label="Person" k="name" sort={sort} />
                <SortTh label="Days due" k="days_due" sort={sort} />
                <SortTh label="Present" k="attended" sort={sort} />
                <SortTh label="Absent" k="absent_days" sort={sort} />
                <SortTh label="Late" k="late_days" sort={sort} />
                <SortTh label="Est." k="open_days" sort={sort} />
                <SortTh label="Hours worked" k="worked_minutes" sort={sort} />
                <SortTh label="Hours due" k="target_hours" sort={sort} />
                <SortTh label="Difference" k="delta" sort={sort} />
                <SortTh label="Late time" k="late_minutes" sort={sort} />
              </tr></thead>
              <tbody>
                {sort.sortRows(rows, { name: r => r.name || '', period: r => r.period || '', delta: r => (r.worked_minutes || 0) / 60 - Number(r.target_hours || 0), target_hours: r => Number(r.target_hours || 0) }).map(r => {
                  const workedH = (r.worked_minutes || 0) / 60;
                  const targetH = Number(r.target_hours || 0);
                  const delta = workedH - targetH;
                  return (
                    <tr key={r.person_id + (r.period || '')} style={{ borderBottom: `1px solid ${t.border}` }}>
                      {tab !== 'person' && <td style={td}>{r.period}</td>}
                      <td style={{ ...td, fontWeight: 600, cursor: 'pointer' }} onClick={() => openPerson(r.person_id)}>{r.name}</td>
                      <td style={td}>{r.days_due}</td>
                      <td style={td}>{r.attended}</td>
                      <td style={{ ...td, color: r.absent_days ? t.red : t.muted }}>{r.absent_days}</td>
                      <td style={{ ...td, color: r.late_days ? t.amber : t.muted }}>{r.late_days}</td>
                      <td style={{ ...td, color: r.open_days ? t.blue : t.muted }}>{r.open_days || 0}</td>
                      <td style={td}>{hm(r.worked_minutes)}</td>
                      <td style={{ ...td, color: t.muted }}>{targetH ? targetH + 'h' : '—'}</td>
                      <td style={{ ...td, color: delta >= 0 ? t.green : t.red, fontWeight: 600 }}>
                        {targetH ? (delta >= 0 ? '+' : '') + delta.toFixed(1) + 'h' : '—'}
                      </td>
                      <td style={{ ...td, color: r.late_minutes ? t.amber : t.muted }}>{hm(r.late_minutes)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ fontSize: 11, color: t.muted, padding: '8px 12px' }}>
              Hours due = contracted hours per day × days that person was scheduled to work.
              “Open” = checked in but never checked out.
            </div>
          </>
        )}
      </div>
      {records && <RecordsModal data={records} onClose={() => setRecords(null)} />}
    </div>
  );
}

// Shows the raw in/out swipe records for a day, with the time worked in each segment
// and the break gaps between them.
function RecordsModal({ data, onClose }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(20,26,38,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80, padding: 20 }}>
      <div style={{ ...panel, width: 460, maxWidth: '100%', maxHeight: '86vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: `1px solid ${t.border}` }}>
          <div><div style={{ fontSize: 15, fontWeight: 800 }}>{data.name}</div><div style={{ fontSize: 12, color: t.muted }}>{data.date} · in / out records</div></div>
          <div style={{ flex: 1 }} />
          <button style={ghost} onClick={onClose}>Close</button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 13, marginBottom: 12 }}>Actual worked: <b style={{ color: t.brand }}>{hm(data.net)}</b> <span style={{ color: t.muted }}>(breaks subtracted)</span></div>
          {data.segments.map((s, i) => (
            <div key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#f0fdfa', borderRadius: 8, marginBottom: 4 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{s.in}</span>
                <span style={{ color: t.muted }}>→</span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{s.out || '— (no check-out)'}</span>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: t.green, fontWeight: 700 }}>{s.minutes != null ? hm(s.minutes) : ''}</span>
              </div>
              {data.gaps[i] && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', marginBottom: 4, fontSize: 11.5, color: t.amber }}>
                  <span>⏸ out {data.gaps[i].from} → back {data.gaps[i].to}</span>
                  <div style={{ flex: 1 }} />
                  <span>break {hm(data.gaps[i].minutes)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PersonView({ me, pid, back }) {
  const [data, setData] = useState(null);
  const [editReason, setEditReason] = useState(null);
  const [records, setRecords] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (from) p.set('from', from); if (to) p.set('to', to);
    attendanceApi.get(`/people/${pid}?` + p.toString()).then(setData).catch(() => setData(null));
  }, [pid, from, to]);
  useEffect(() => { load(); }, [load]);
  const setMonth = (offset) => {
    const d = new Date(); d.setMonth(d.getMonth() + offset);
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, d.getMonth() + 1, 0).getDate();
    setFrom(`${y}-${m}-01`); setTo(`${y}-${m}-${String(last).padStart(2, '0')}`);
  };
  const exportPerson = async () => {
    try {
      const token = localStorage.getItem('flowup_token');
      const p = new URLSearchParams(); if (from) p.set('from', from); if (to) p.set('to', to);
      const resp = await fetch(`/api/attendance/export/person/${pid}.xlsx?` + p.toString(), { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' });
      if (!resp.ok) throw new Error();
      const a = document.createElement('a'); a.href = URL.createObjectURL(await resp.blob()); a.download = `attendance_${pid}.xlsx`; a.click();
    } catch (e) { alert('Export failed.'); }
  };
  if (!data) return <div style={{ color: t.muted }}>Loading…</div>;
  const { person, assignments, leave, days } = data;
  const saveReason = async (d, reason) => { await attendanceApi.put(`/attendance/${pid}/${d}/reason`, { reason }); setEditReason(null); load(); };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <button onClick={back} style={ghost}>← People</button>
        <div style={{ flex: 1 }} />
        {me?.can_export && <button onClick={exportPerson} style={btn}>⬇ Export to Excel</button>}
      </div>
      {/* date range filter for this person */}
      <div style={{ ...panel, padding: 12, marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div><label style={labelCss}>From</label><input type="date" value={from} style={inputCss} onChange={e => setFrom(e.target.value)} /></div>
        <div><label style={labelCss}>To</label><input type="date" value={to} style={inputCss} onChange={e => setTo(e.target.value)} /></div>
        <button style={ghost} onClick={() => setMonth(0)}>This month</button>
        <button style={ghost} onClick={() => setMonth(-1)}>Last month</button>
        <button style={ghost} onClick={() => { setFrom(''); setTo(''); }}>Recent (90 days)</button>
      </div>
      <div style={{ ...panel, padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 800 }}>{person.display_name}</div>
        <div style={{ fontSize: 13, color: t.muted, marginTop: 4 }}>Card {person.person_id} · {person.org || '—'} · {person.job_title || '—'}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ ...panel, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Card holders</div>
          {assignments.length === 0 && <div style={{ color: t.muted, fontSize: 13 }}>No assignments.</div>}
          {assignments.map(a => (<div key={a.id} style={{ fontSize: 12.5, padding: '5px 0', borderBottom: `1px solid ${t.border}` }}><b>{a.holder_name}</b> · {a.start_date} → {a.end_date || 'present'}</div>))}
        </div>
        <div style={{ ...panel, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Leave</div>
          {leave.length === 0 && <div style={{ color: t.muted, fontSize: 13 }}>No leave recorded.</div>}
          {leave.map(l => (<div key={l.id} style={{ fontSize: 12.5, padding: '5px 0', borderBottom: `1px solid ${t.border}` }}>{l.kind} · {l.start_date} → {l.end_date}{l.note ? ` · ${l.note}` : ''}</div>))}
        </div>
      </div>
      <div style={{ ...panel, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>{['Date', 'In', 'Out', 'Worked', 'Actual worked', 'Late', 'Day', 'Status', 'Reason', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {days.map(d => (
              <tr key={d.work_date} style={{ borderBottom: `1px solid ${t.border}` }}>
                <td style={{ ...td, color: t.muted }}>{d.work_date}</td><td style={td}>{d.first_in || '—'}</td><td style={td}>{d.last_out || '—'}</td>
                <td style={td}>{hm(d.worked_minutes)}</td>
                <td style={{ ...td, cursor: (d.segments && d.segments.length) ? 'pointer' : 'default', color: t.brand, fontWeight: 600 }} title="Click to see in/out records" onClick={() => { if (d.segments && d.segments.length) setRecords({ name: person.display_name, date: d.work_date, segments: d.segments, gaps: d.gaps, net: d.net_minutes }); }}>{hm(d.net_minutes)}{(d.segments && d.segments.length > 1) && <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.7 }}>▦</span>}</td>
                <td style={{ ...td, color: d.late_minutes ? t.amber : t.muted }}>{d.late_minutes ? d.late_minutes + 'm' : '—'}</td>
                <td style={td}><DayTrack firstIn={d.first_in} lastOut={d.last_out} shiftStart={d.shift_start} shiftEnd={d.shift_end} status={d.status} /></td>
                <td style={td}><StatusPill s={d.status} />{d.late_minutes > 0 && <span title={`Late ${d.late_minutes}m`} style={{ fontSize: 9, color: '#fff', background: t.amber, borderRadius: 4, padding: '1px 5px', marginLeft: 6, fontWeight: 700 }}>LATE</span>}</td><td style={{ ...td, color: t.muted, fontSize: 12 }}>{d.reason || ''}</td>
                <td style={td}>{me?.can_manage_leave && <button onClick={() => setEditReason(d.work_date)} style={{ ...ghost, padding: '3px 8px', fontSize: 11 }}>Reason</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editReason && <ReasonModal date={editReason} current={days.find(d => d.work_date === editReason)?.reason} onClose={() => setEditReason(null)} onSave={saveReason} />}
      {records && <RecordsModal data={records} onClose={() => setRecords(null)} />}
    </div>
  );
}
function ReasonModal({ date, current, person, onClose, onSave }) {
  const [r, setR] = useState(current || '');
  return <FullModal title={`Reason — ${date}`} subtitle={person || ''} onClose={onClose}>
    <div style={{ maxWidth: 720 }}>
      <Field label="Reason for lateness / absence">
        <textarea rows={6} style={{ ...inputCss, resize: 'vertical', minHeight: 140 }} value={r} onChange={e => setR(e.target.value)} autoFocus />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button style={ghost} onClick={onClose}>Cancel</button><button style={btn} onClick={() => onSave(date, r)}>Save reason</button></div>
    </div>
  </FullModal>;
}

function PeopleView({ me, openPerson }) {
  const [people, setPeople] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [handover, setHandover] = useState(null);
  const [profile, setProfile] = useState(null);
  const [q, setQ] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [schedFilter, setSchedFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('active');
  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (groupFilter) params.set('group_id', groupFilter);
    params.set('include_archived', '1'); // load everyone; filter client-side
    attendanceApi.get('/people?' + params.toString()).then(setPeople).catch(() => setPeople([]));
  }, [groupFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { attendanceApi.get('/schedules').then(setSchedules).catch(() => {}); attendanceApi.get('/groups').then(setGroups).catch(() => {}); }, []);

  const isInactive = (p) => p.archived === 1 || p.archived === true;
  const filtered = people.filter(p => {
    if (activeFilter === 'active' && isInactive(p)) return false;
    if (activeFilter === 'inactive' && !isInactive(p)) return false;
    if (schedFilter && String(p.schedule_id || '') !== String(schedFilter)) return false;
    if (!q) return true;
    const hay = `${p.name || p.display_name} ${p.person_id} ${p.employee_no || ''} ${p.department || ''}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });
  const sort = useSort('name', 'asc');
  const visible = sort.sortRows(filtered, {
    person_id: p => p.person_id || '', name: p => p.name || p.display_name || '',
    schedule_name: p => p.schedule_name || '', group_name: p => p.group_name || '',
    department: p => p.department || '', last_seen: p => p.last_seen || '',
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>People</h2>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: t.muted }}>{visible.length} of {people.length} cards{me?.group_id ? ' · your group' : ''}</span>
      </div>

      {/* professional filters (item 5) */}
      <div style={{ ...panel, padding: 12, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 200 }}>
          <label style={labelCss}>Search</label>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Name, card, employee no, department…" style={inputCss} />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label style={labelCss}>Group</label>
          <select value={groupFilter} style={inputCss} onChange={e => setGroupFilter(e.target.value)}>
            <option value="">All groups</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            <option value="none">Not in a group</option>
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <label style={labelCss}>Schedule</label>
          <select value={schedFilter} style={inputCss} onChange={e => setSchedFilter(e.target.value)}>
            <option value="">All schedules</option>
            {schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 130 }}>
          <label style={labelCss}>Status</label>
          <select value={activeFilter} style={inputCss} onChange={e => setActiveFilter(e.target.value)}>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      <div style={{ ...panel, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>
            <SortTh label="Card" k="person_id" sort={sort} />
            <SortTh label="Name" k="name" sort={sort} />
            <SortTh label="Schedule" k="schedule_name" sort={sort} />
            <SortTh label="Group" k="group_name" sort={sort} />
            <SortTh label="Department" k="department" sort={sort} />
            <SortTh label="Last seen" k="last_seen" sort={sort} />
            {me?.is_admin && <th style={th}></th>}
          </tr></thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: t.muted }}>No people match.</td></tr>}
            {visible.map(pn => (
              <tr key={pn.person_id} style={{ borderBottom: `1px solid ${t.border}`, cursor: 'pointer' }} onClick={() => setProfile(pn)}>
                <td style={{ ...td, fontFamily: 'monospace', color: t.brand }}>{pn.person_id}</td>
                <td style={{ ...td, fontWeight: 600 }}>{pn.name || pn.display_name}{isInactive(pn) && <span style={{ fontSize: 9, color: '#fff', background: t.red, borderRadius: 4, padding: '1px 5px', marginLeft: 6, fontWeight: 700 }}>INACTIVE</span>}</td>
                <td style={{ ...td, color: t.muted, fontSize: 12 }}>{pn.schedule_name || 'Default'}</td>
                <td style={{ ...td, color: t.muted, fontSize: 12 }}>{pn.group_name || '—'}</td>
                <td style={{ ...td, color: t.muted, fontSize: 12 }}>{pn.department || '—'}</td>
                <td style={{ ...td, color: t.muted }}>{pn.last_seen || '—'}</td>
                {me?.is_admin && <td style={td} onClick={e => e.stopPropagation()}><div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...ghost, padding: '3px 8px', fontSize: 11 }} onClick={() => setProfile(pn)}>Profile</button>
                  <button style={{ ...ghost, padding: '3px 8px', fontSize: 11 }} onClick={() => setHandover(pn)}>Handover</button>
                </div></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {handover && <HandoverModal person={handover} schedules={schedules} groups={groups} onClose={() => setHandover(null)} onDone={() => { setHandover(null); load(); }} />}
      {profile && <ProfileModal person={profile} me={me} schedules={schedules} groups={groups} openPerson={openPerson} onClose={() => setProfile(null)} onDone={() => { setProfile(null); load(); }} />}
    </div>
  );
}

// Full-page profile pop-out (item 5): everything about a person, editable, and their
// recent day history. Saving persists name/schedule/group/dept and RE-SCORES.
function ProfileModal({ person, me, schedules, groups, openPerson, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    attendanceApi.get(`/people/${person.person_id}`).then(d => {
      setData(d);
      const cur = d.current || {};
      setF({
        custom_name: d.person.custom_name || '',
        holder_name: cur.holder_name || d.person.display_name || '',
        employee_no: cur.employee_no || '',
        department: cur.department || '',
        schedule_id: cur.schedule_id || '',
        group_id: cur.group_id || '',
      });
    }).catch(() => setData({ error: true }));
  }, [person.person_id]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const isActive = data && data.person && !(data.person.archived === 1 || data.person.archived === true);
  const toggleActive = async () => {
    if (!me?.is_admin || !data) return;
    const makeActive = !isActive;
    try {
      await attendanceApi.post(`/people/${person.person_id}/archive`, { archived: makeActive ? false : true });
      setData(d => ({ ...d, person: { ...d.person, archived: makeActive ? 0 : 1 } }));
      onDone();
    } catch (e) { alert(e?.error || 'Failed'); }
  };
  const save = async () => {
    if (!me?.is_admin) return;
    setSaving(true); setMsg('');
    try {
      const r = await attendanceApi.put(`/people/${person.person_id}/profile`, {
        custom_name: f.custom_name, holder_name: f.holder_name, employee_no: f.employee_no,
        department: f.department, schedule_id: f.schedule_id || null, group_id: f.group_id || null,
      });
      setMsg(`Saved. Re-scored ${r.rescored ?? 0} report(s).`);
      setTimeout(() => onDone(), 700);
    } catch (e) { setMsg(e?.error || 'Save failed.'); } finally { setSaving(false); }
  };

  return (
    <FullModal title={person.name || person.display_name || person.person_id} subtitle={`Card ${person.person_id}`} onClose={onClose}>
      {!data && <div style={{ color: t.muted, padding: 20 }}>Loading…</div>}
      {data && !f && <div style={{ color: t.red, padding: 20 }}>Could not load this person.</div>}
      {data && f && (
        <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: 24 }}>
          {/* left: editable profile */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: t.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>Profile</div>
            <Field label="Display name (override)"><input style={inputCss} value={f.custom_name} onChange={e => set('custom_name', e.target.value)} placeholder={data.person.display_name} disabled={!me?.is_admin} /></Field>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><Field label="Employee no."><input style={inputCss} value={f.employee_no} onChange={e => set('employee_no', e.target.value)} disabled={!me?.is_admin} /></Field></div>
              <div style={{ flex: 1 }}><Field label="Department"><input style={inputCss} value={f.department} onChange={e => set('department', e.target.value)} disabled={!me?.is_admin} /></Field></div>
            </div>
            <Field label="Schedule"><select style={inputCss} value={f.schedule_id} onChange={e => set('schedule_id', e.target.value)} disabled={!me?.is_admin}><option value="">Default</option>{schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field>
            <Field label="Group"><select style={inputCss} value={f.group_id} onChange={e => set('group_id', e.target.value)} disabled={!me?.is_admin}><option value="">None</option>{groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select></Field>
            {me?.is_admin && <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
              <button style={btn} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              <button style={ghost} onClick={() => { onClose(); openPerson(person.person_id); }}>Full record →</button>
              {msg && <span style={{ fontSize: 12, color: t.green }}>{msg}</span>}
            </div>}
            {me?.is_admin && data && (
              <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>Status: <span style={{ color: isActive ? t.green : t.red }}>{isActive ? 'Active' : 'Inactive'}</span></div>
                  <div style={{ fontSize: 11, color: t.muted }}>{isActive ? 'Included in all reports, dashboard and totals.' : 'Excluded from all reports, dashboard and totals.'}</div>
                </div>
                <button style={{ ...ghost, color: isActive ? t.red : t.green, borderColor: isActive ? '#f3c2c2' : '#bfe6c9' }} onClick={toggleActive}>
                  {isActive ? 'Set inactive' : 'Set active'}
                </button>
              </div>
            )}
            <p style={{ fontSize: 11, color: t.muted, marginTop: 10 }}>Changing the schedule re-scores this person's history immediately.</p>

            <div style={{ fontSize: 12, fontWeight: 800, color: t.muted, textTransform: 'uppercase', letterSpacing: '.5px', margin: '18px 0 8px' }}>Card history</div>
            {(data.assignments || []).length === 0 && <div style={{ color: t.muted, fontSize: 13 }}>No assignments.</div>}
            {(data.assignments || []).map(a => (
              <div key={a.id} style={{ fontSize: 12.5, padding: '5px 0', borderBottom: `1px solid ${t.border}` }}><b>{a.holder_name}</b> · {a.start_date} → {a.end_date || 'present'}</div>
            ))}
          </div>

          {/* right: recent days + leave */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: t.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 12 }}>Recent days</div>
            <div style={{ ...panel, overflow: 'auto', maxHeight: 360 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>{['Date', 'In', 'Out', 'Worked', 'Late', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {(data.days || []).slice(0, 40).map(d => (
                    <tr key={d.work_date} style={{ borderBottom: `1px solid ${t.border}` }}>
                      <td style={{ ...td, color: t.muted }}>{d.work_date}</td>
                      <td style={td}>{d.first_in || '—'}</td><td style={td}>{d.last_out || '—'}</td>
                      <td style={td}>{hm(d.worked_minutes)}</td>
                      <td style={{ ...td, color: d.late_minutes ? t.amber : t.muted }}>{d.late_minutes ? d.late_minutes + 'm' : '—'}</td>
                      <td style={td}><StatusPill s={d.status} />{d.late_minutes > 0 && <span title={`Late ${d.late_minutes}m`} style={{ fontSize: 9, color: '#fff', background: t.amber, borderRadius: 4, padding: '1px 4px', marginLeft: 5, fontWeight: 700 }}>LATE</span>}</td>
                    </tr>
                  ))}
                  {(data.days || []).length === 0 && <tr><td colSpan={6} style={{ padding: 16, color: t.muted, textAlign: 'center' }}>No records yet.</td></tr>}
                </tbody>
              </table>
            </div>
            {(data.leave || []).length > 0 && <>
              <div style={{ fontSize: 12, fontWeight: 800, color: t.muted, textTransform: 'uppercase', letterSpacing: '.5px', margin: '18px 0 8px' }}>Leave</div>
              {data.leave.map(l => <div key={l.id} style={{ fontSize: 12.5, padding: '4px 0' }}>{l.start_date} → {l.end_date} · <span style={{ color: t.violet }}>{l.kind}</span>{l.note ? ` · ${l.note}` : ''}</div>)}
            </>}
          </div>
        </div>
      )}
    </FullModal>
  );
}


function HandoverModal({ person, schedules, groups, onClose, onDone }) {
  const [f, setF] = useState({ holder_name: '', employee_no: '', department: '', schedule_id: '', group_id: '', start_date: today(), notes: '' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.holder_name.trim()) { alert('Enter the new holder name.'); return; }
    await attendanceApi.post(`/people/${person.person_id}/handover`, { ...f, schedule_id: f.schedule_id || null, group_id: f.group_id || null }); onDone();
  };
  return <Modal title={`Hand over card ${person.person_id}`} onClose={onClose}>
    <Field label="New holder name"><input style={inputCss} value={f.holder_name} onChange={e => set('holder_name', e.target.value)} /></Field>
    <Field label="Start date"><input type="date" style={inputCss} value={f.start_date} onChange={e => set('start_date', e.target.value)} /></Field>
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ flex: 1 }}><Field label="Employee no."><input style={inputCss} value={f.employee_no} onChange={e => set('employee_no', e.target.value)} /></Field></div>
      <div style={{ flex: 1 }}><Field label="Department"><input style={inputCss} value={f.department} onChange={e => set('department', e.target.value)} /></Field></div>
    </div>
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ flex: 1 }}><Field label="Schedule"><select style={inputCss} value={f.schedule_id} onChange={e => set('schedule_id', e.target.value)}><option value="">Default</option>{schedules.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></Field></div>
      <div style={{ flex: 1 }}><Field label="Group"><select style={inputCss} value={f.group_id} onChange={e => set('group_id', e.target.value)}><option value="">None</option>{groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select></Field></div>
    </div>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}><button style={ghost} onClick={onClose}>Cancel</button><button style={btn} onClick={save}>Hand over</button></div>
    <p style={{ fontSize: 11, color: t.muted, marginTop: 10 }}>The previous holder's record is closed the day before; records stay attributed to whoever held the card on each date.</p>
  </Modal>;
}
function LeaveView() {
  const [people, setPeople] = useState([]);
  const [f, setF] = useState({ person_id: '', start_date: today(), end_date: today(), kind: 'annual', note: '' });
  const [msg, setMsg] = useState('');
  useEffect(() => { attendanceApi.get('/people').then(setPeople).catch(() => {}); }, []);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.person_id) { setMsg('Select a person.'); return; }
    try { await attendanceApi.post('/leave', f); setMsg('Leave recorded.'); } catch (e) { setMsg(e?.error || 'Failed.'); }
  };
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 16px' }}>Record leave</h2>
      <div style={{ ...panel, padding: 18, maxWidth: 560 }}>
        <Field label="Person"><select style={inputCss} value={f.person_id} onChange={e => set('person_id', e.target.value)}><option value="">Select…</option>{people.map(p => <option key={p.person_id} value={p.person_id}>{p.display_name}</option>)}</select></Field>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Field label="From"><input type="date" style={inputCss} value={f.start_date} onChange={e => set('start_date', e.target.value)} /></Field></div>
          <div style={{ flex: 1 }}><Field label="To"><input type="date" style={inputCss} value={f.end_date} onChange={e => set('end_date', e.target.value)} /></Field></div>
        </div>
        <Field label="Kind"><select style={inputCss} value={f.kind} onChange={e => set('kind', e.target.value)}>{['annual', 'sick', 'unpaid', 'remote', 'other'].map(k => <option key={k} value={k}>{k}</option>)}</select></Field>
        <Field label="Note"><input style={inputCss} value={f.note} onChange={e => set('note', e.target.value)} /></Field>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}><button style={btn} onClick={submit}>Record leave</button>{msg && <span style={{ fontSize: 13, color: msg.includes('recorded') ? t.green : t.red }}>{msg}</span>}</div>
        <p style={{ fontSize: 11, color: t.muted, marginTop: 12 }}>Only days the person was due to work are affected; days with a swipe are left alone. Remote days are credited as a full day.</p>
      </div>
    </div>
  );
}

function ImportView() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const onFile = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
      const r = await attendanceApi.post('/import', { file_base64: b64, filename: file.name });
      setResult(r);
    } catch (er) { setError(er?.error || 'Import failed.'); } finally { setBusy(false); }
  };
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px' }}>Import report</h2>
      <div style={{ fontSize: 12.5, color: t.muted, marginBottom: 16 }}>Upload the daily access-control report (.xls from the portal). Re-importing the same day updates figures but keeps recorded reasons.</div>
      <div style={{ ...panel, padding: 24, maxWidth: 560 }}>
        <label style={labelCss}>Report file</label>
        <input type="file" accept=".xls,.html,.htm" onChange={onFile} disabled={busy} style={{ fontSize: 13 }} />
        {busy && <div style={{ color: t.muted, marginTop: 14 }}>Importing…</div>}
        {error && <div style={{ color: t.red, marginTop: 14, fontSize: 13 }}>{error}</div>}
        {result && <div style={{ marginTop: 16, padding: 14, background: 'var(--bg)', borderRadius: 8, fontSize: 13 }}>
          <div style={{ color: t.green, fontWeight: 700, marginBottom: 6 }}>✓ Imported</div>
          <div>Work date: <b>{result.work_date}</b></div><div>People: <b>{result.people}</b> ({result.created} new cards)</div>
        </div>}
      </div>
    </div>
  );
}

function SchedulesView() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => { attendanceApi.get('/schedules').then(setRows).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}><h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Schedules</h2><div style={{ flex: 1 }} /><button style={btn} onClick={() => setEdit({})}>+ New schedule</button></div>
      <div style={{ ...panel, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>{['Name', 'Hours', 'Workdays', 'Lateness', 'Holidays', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.map(s => (
              <tr key={s.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                <td style={{ ...td, fontWeight: 600 }}>{s.name}{s.is_default ? <span style={{ fontSize: 10, color: t.brand, marginLeft: 6 }}>default</span> : ''}</td>
                <td style={{ ...td, color: t.muted }}>{s.shift_start}–{s.shift_end}</td><td style={{ ...td, color: t.muted }}>{s.workdays}</td>
                <td style={{ ...td, color: t.muted }}>{s.track_lateness ? 'tracked' : 'hours only'}</td>
                <td style={{ ...td, color: t.muted }}>{s.observes_holidays ? 'observed' : 'worked through'}</td>
                <td style={td}><button style={{ ...ghost, padding: '3px 8px', fontSize: 11 }} onClick={() => setEdit(s)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <ScheduleModal sched={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}
function ScheduleModal({ sched, onClose, onDone }) {
  const isNew = !sched.id;
  const [f, setF] = useState({
    name: sched.name || '', kind: sched.kind || 'standard', shift_start: sched.shift_start || '07:30', shift_end: sched.shift_end || '15:30',
    workdays: sched.workdays || 'Sun,Mon,Tue,Wed,Thu', on_time: sched.on_time || '09:00', early_before: sched.early_before || '15:00',
    daily_hours: sched.daily_hours || 8, weekly_target: sched.weekly_target || 40,
    observes_holidays: sched.observes_holidays ?? 1, track_lateness: sched.track_lateness ?? 1,
    dual_shift: sched.dual_shift ?? 0, shift2_start: sched.shift2_start || '14:00', shift2_end: sched.shift2_end || '22:00',
    shift2_on_time: sched.shift2_on_time || '14:00', split_time: sched.split_time || '11:00',
    bands: sched.bands || '[]',
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const toggleDay = (d) => { const s0 = new Set(f.workdays.split(',').filter(Boolean)); s0.has(d) ? s0.delete(d) : s0.add(d); set('workdays', days.filter(x => s0.has(x)).join(',')); };
  const save = async () => {
    if (!f.name.trim()) { alert('Name required.'); return; }
    const body = { ...f, observes_holidays: !!f.observes_holidays, track_lateness: !!f.track_lateness, dual_shift: !!f.dual_shift };
    if (isNew) await attendanceApi.post('/schedules', body); else await attendanceApi.put(`/schedules/${sched.id}`, body);
    onDone();
  };
  return <Modal title={isNew ? 'New schedule' : `Edit — ${sched.name}`} onClose={onClose} width={560}>
    <Field label="Name"><input style={inputCss} value={f.name} onChange={e => set('name', e.target.value)} /></Field>
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ flex: 1 }}><Field label="Shift start"><input style={inputCss} value={f.shift_start} onChange={e => set('shift_start', e.target.value)} /></Field></div>
      <div style={{ flex: 1 }}><Field label="Shift end"><input style={inputCss} value={f.shift_end} onChange={e => set('shift_end', e.target.value)} /></Field></div>
      <div style={{ flex: 1 }}><Field label="Daily hours"><input type="number" style={inputCss} value={f.daily_hours} onChange={e => set('daily_hours', e.target.value)} /></Field></div>
    </div>
    <Field label="Working days"><div style={{ display: 'flex', gap: 6 }}>{days.map(d => <button key={d} onClick={() => toggleDay(d)} style={{ ...(f.workdays.includes(d) ? btn : ghost), padding: '6px 9px', fontSize: 12 }}>{d}</button>)}</div></Field>
    <div style={{ display: 'flex', gap: 10 }}>
      <div style={{ flex: 1 }}><Field label="Late after (morning start)"><input style={inputCss} value={f.on_time} onChange={e => set('on_time', e.target.value)} /></Field></div>
      <div style={{ flex: 1 }}><Field label="Early-leave before"><input style={inputCss} value={f.early_before} onChange={e => set('early_before', e.target.value)} /></Field></div>
    </div>
    <div style={{ display: 'flex', gap: 18, margin: '6px 0 10px' }}>
      <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={!!f.track_lateness} onChange={e => set('track_lateness', e.target.checked ? 1 : 0)} /> Track lateness</label>
      <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}><input type="checkbox" checked={!!f.observes_holidays} onChange={e => set('observes_holidays', e.target.checked ? 1 : 0)} /> Observes public holidays</label>
      <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer', fontWeight: 700, color: t.brand }}><input type="checkbox" checked={!!f.dual_shift} onChange={e => set('dual_shift', e.target.checked ? 1 : 0)} /> Two shifts (roster)</label>
    </div>
    {!!f.dual_shift && (
      <div style={{ ...panel, padding: 14, marginBottom: 12, background: 'var(--bg)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: t.muted, marginBottom: 8 }}>SECOND (AFTERNOON) SHIFT</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Field label="Shift 2 start"><input style={inputCss} value={f.shift2_start} onChange={e => set('shift2_start', e.target.value)} /></Field></div>
          <div style={{ flex: 1 }}><Field label="Shift 2 end"><input style={inputCss} value={f.shift2_end} onChange={e => set('shift2_end', e.target.value)} /></Field></div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Field label="Late after (shift 2)"><input style={inputCss} value={f.shift2_on_time} onChange={e => set('shift2_on_time', e.target.value)} /></Field></div>
          <div style={{ flex: 1 }}><Field label="Morning / afternoon split"><input style={inputCss} value={f.split_time} onChange={e => set('split_time', e.target.value)} /></Field></div>
        </div>
        <p style={{ fontSize: 11, color: t.muted, margin: 0 }}>Check-in before the split time is judged against the morning shift; at or after it, against the afternoon shift. Arriving before a shift's start is flagged.</p>
      </div>
    )}
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button style={ghost} onClick={onClose}>Cancel</button><button style={btn} onClick={save}>Save</button></div>
  </Modal>;
}

function GroupsView() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => { attendanceApi.get('/groups').then(setRows).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const send = async (g) => { try { const r = await attendanceApi.post(`/groups/${g.id}/send`); setMsg(r.message || 'Queued.'); } catch (e) { setMsg(e?.error || 'Failed.'); } };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}><h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Groups</h2><div style={{ flex: 1 }} /><button style={btn} onClick={() => setEdit({})}>+ New group</button></div>
      {msg && <div style={{ ...panel, padding: 10, marginBottom: 12, fontSize: 13, color: t.blue }}>{msg}</div>}
      <div style={{ ...panel, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>{['Name', 'Manager', 'Email', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: t.muted }}>No groups yet.</td></tr>}
            {rows.map(g => (
              <tr key={g.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                <td style={{ ...td, fontWeight: 600 }}>{g.name}</td><td style={{ ...td, color: t.muted }}>{g.manager_name || '—'}</td><td style={{ ...td, color: t.muted }}>{g.manager_email || '—'}</td>
                <td style={td}><div style={{ display: 'flex', gap: 6 }}><button style={{ ...ghost, padding: '3px 8px', fontSize: 11 }} onClick={() => setEdit(g)}>Edit</button><button style={{ ...ghost, padding: '3px 8px', fontSize: 11 }} onClick={() => send(g)}>Send report</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <GroupModal group={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}
function GroupModal({ group, onClose, onDone }) {
  const isNew = !group.id;
  const [f, setF] = useState({ name: group.name || '', manager_name: group.manager_name || '', manager_email: group.manager_email || '', notes: group.notes || '', send_monthly: group.send_monthly ?? 1 });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { alert('Name required.'); return; }
    const body = { ...f, send_monthly: !!f.send_monthly };
    if (isNew) await attendanceApi.post('/groups', body); else await attendanceApi.put(`/groups/${group.id}`, body);
    onDone();
  };
  return <Modal title={isNew ? 'New group' : `Edit — ${group.name}`} onClose={onClose}>
    <Field label="Name"><input style={inputCss} value={f.name} onChange={e => set('name', e.target.value)} /></Field>
    <Field label="Manager name"><input style={inputCss} value={f.manager_name} onChange={e => set('manager_name', e.target.value)} /></Field>
    <Field label="Manager email"><input style={inputCss} value={f.manager_email} onChange={e => set('manager_email', e.target.value)} /></Field>
    <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer', marginBottom: 14 }}><input type="checkbox" checked={!!f.send_monthly} onChange={e => set('send_monthly', e.target.checked ? 1 : 0)} /> Send monthly report automatically</label>
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button style={ghost} onClick={onClose}>Cancel</button><button style={btn} onClick={save}>Save</button></div>
  </Modal>;
}

function SettingsView() {
  const [s, setS] = useState({});
  const [holidays, setHolidays] = useState([]);
  const { toast, show } = useToast();
  const [newHol, setNewHol] = useState({ holiday_date: today(), name: '' });
  const load = useCallback(() => { attendanceApi.get('/settings').then(v => setS(v || {})).catch(() => {}); attendanceApi.get('/holidays').then(setHolidays).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setS(p => ({ ...p, [k]: v }));
  const saveSection = async (keys, what = 'Settings') => {
    const body = {}; keys.forEach(k => { body[k] = s[k] ?? ''; });
    try { await attendanceApi.put('/settings', body); await load(); show(`${what} saved.`); }
    catch (e) { show(e?.error || 'Save failed.', 'error'); }
  };
  const addHoliday = async () => { if (!newHol.holiday_date) return; try { await attendanceApi.post('/holidays', newHol); setNewHol({ holiday_date: today(), name: '' }); load(); show('Holiday added.'); } catch (e) { show(e?.error || 'Failed.', 'error'); } };
  const delHoliday = async (d) => { try { await attendanceApi.delete(`/holidays/${d}`); load(); show('Holiday removed.'); } catch (e) { show(e?.error || 'Failed.', 'error'); } };
  const recalc = async () => { show('Recalculating…', 'info'); try { const r = await attendanceApi.post('/recalculate'); show(`Recalculated${r?.rescored != null ? ` ${r.rescored} report(s)` : ''}${r?.failures?.length ? `, ${r.failures.length} failed` : ''}.`, r?.failures?.length ? 'error' : 'success'); } catch (e) { show(e?.error || 'Failed.', 'error'); } };
  const testEmail = async () => { show('Sending test…', 'info'); try { const r = await attendanceApi.post('/email/test'); show(r.message || 'Test email sent.'); } catch (e) { show(e?.error || 'Failed.', 'error'); } };
  const testMailbox = async () => { show('Testing mailbox…', 'info'); try { const r = await attendanceApi.post('/settings/test'); show(r.message || 'Connected.'); } catch (e) { show(e?.error || 'Failed.', 'error'); } };
  const fetchNow = async () => { show('Checking mailbox…', 'info'); try { const r = await attendanceApi.post('/fetch'); show(r.message || 'Done.'); } catch (e) { show(e?.error || 'Failed.', 'error'); } };
  return (
    <div style={{ maxWidth: 720 }}>
      <Toast toast={toast} />
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}><h2 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Settings</h2><div style={{ flex: 1 }} /><button style={ghost} onClick={recalc}>Recalculate</button></div>

      <SettingsCard title="Mailbox (auto-collect the nightly report)">
        <Row><Half label="IMAP host"><input style={inputCss} value={s.mail_host || ''} onChange={e => set('mail_host', e.target.value)} /></Half><Half label="Port"><input style={inputCss} value={s.mail_port || ''} onChange={e => set('mail_port', e.target.value)} /></Half></Row>
        <Row><Half label="Username"><input style={inputCss} value={s.mail_user || ''} onChange={e => set('mail_user', e.target.value)} /></Half><Half label="Password"><input type="password" placeholder="(unchanged)" style={inputCss} value={s.mail_password || ''} onChange={e => set('mail_password', e.target.value)} /></Half></Row>
        <Row><Half label="Folder"><input style={inputCss} value={s.mail_folder || 'INBOX'} onChange={e => set('mail_folder', e.target.value)} /></Half><Half label="Subject contains"><input style={inputCss} value={s.mail_subject || ''} onChange={e => set('mail_subject', e.target.value)} /></Half></Row>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn} onClick={() => saveSection(['mail_host', 'mail_port', 'mail_user', 'mail_password', 'mail_folder', 'mail_subject', 'filename_hints', 'mail_search_days'], 'Mailbox')}>Save mailbox</button>
          <button style={ghost} onClick={testMailbox}>Test connection</button>
          <button style={ghost} onClick={fetchNow}>Fetch now</button>
        </div>
      </SettingsCard>

      <SettingsCard title="Checking schedule & retention">
        <Row><Half label="Window start"><input style={inputCss} value={s.window_start || '22:50'} onChange={e => set('window_start', e.target.value)} /></Half><Half label="Window end"><input style={inputCss} value={s.window_end || '23:30'} onChange={e => set('window_end', e.target.value)} /></Half></Row>
        <Row><Half label="Poll every (min)"><input style={inputCss} value={s.poll_minutes || '5'} onChange={e => set('poll_minutes', e.target.value)} /></Half><Half label="Retention (months)"><input style={inputCss} value={s.retention_months || '12'} onChange={e => set('retention_months', e.target.value)} /></Half></Row>
        <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer', margin: '4px 0 12px' }}><input type="checkbox" checked={s.auto_fetch === undefined ? true : s.auto_fetch === '1'} onChange={e => set('auto_fetch', e.target.checked ? '1' : '0')} /> Auto-fetch enabled</label>
        <button style={btn} onClick={() => saveSection(['window_start', 'window_end', 'poll_minutes', 'retention_months', 'auto_fetch'], 'Schedule')}>Save schedule</button>
      </SettingsCard>

      <SettingsCard title="Email (monthly manager reports)">
        <Row><Half label="Weekly target hours"><input style={inputCss} value={s.weekly_target_hours || '40'} onChange={e => set('weekly_target_hours', e.target.value)} /></Half><Half label="Monthly send day"><input style={inputCss} value={s.monthly_send_day || '1'} onChange={e => set('monthly_send_day', e.target.value)} /></Half></Row>
        <Row><Half label="Email subject"><input style={inputCss} value={s.email_subject || ''} onChange={e => set('email_subject', e.target.value)} /></Half><Half label=" "><label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13, cursor: 'pointer', paddingTop: 8 }}><input type="checkbox" checked={s.email_enabled === '1'} onChange={e => set('email_enabled', e.target.checked ? '1' : '0')} /> Email enabled</label></Half></Row>
        <div style={{ display: 'flex', gap: 8 }}><button style={btn} onClick={() => saveSection(['weekly_target_hours', 'monthly_send_day', 'email_subject', 'email_enabled'], 'Email')}>Save email</button><button style={ghost} onClick={testEmail}>Test SMTP</button></div>
        <p style={{ fontSize: 11, color: t.muted, marginTop: 10 }}>Attendance sends through the platform's shared SMTP (main Settings → SMTP). This section controls what and when.</p>
      </SettingsCard>

      <SettingsCard title="Public holidays">
        <Row><Half label="Date"><input type="date" style={inputCss} value={newHol.holiday_date} onChange={e => setNewHol(h => ({ ...h, holiday_date: e.target.value }))} /></Half><Half label="Name"><input style={inputCss} value={newHol.name} onChange={e => setNewHol(h => ({ ...h, name: e.target.value }))} /></Half></Row>
        <button style={btn} onClick={addHoliday}>Add holiday</button>
        <div style={{ marginTop: 12 }}>{holidays.map(h => (
          <div key={h.holiday_date} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: `1px solid ${t.border}`, fontSize: 13 }}>
            <span style={{ fontFamily: 'monospace' }}>{h.holiday_date}</span><span style={{ flex: 1, color: t.muted }}>{h.name || ''}</span>
            <button style={{ ...ghost, padding: '2px 8px', fontSize: 11, color: t.red }} onClick={() => delHoliday(h.holiday_date)}>Remove</button>
          </div>))}
        </div>
      </SettingsCard>
    </div>
  );
}
function SettingsCard({ title, children }) { return <div style={{ ...panel, padding: 18, marginBottom: 16 }}><div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{title}</div>{children}</div>; }
function Row({ children }) { return <div style={{ display: 'flex', gap: 10 }}>{children}</div>; }
function Half({ label, children }) { return <div style={{ flex: 1, marginBottom: 12 }}><label style={labelCss}>{label}</label>{children}</div>; }

function AccessView() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => { attendanceApi.get('/access').then(setData).catch(() => setData({ users: [], groups: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  const update = async (u, patch) => {
    setBusy(u.id);
    try { await attendanceApi.put(`/access/${u.id}`, { level: patch.level ?? u.level, group_id: patch.group_id ?? u.group_id, can_export: patch.can_export ?? u.can_export, can_manage_leave: patch.can_manage_leave ?? u.can_manage_leave }); await load(); }
    catch (e) { alert(e?.error || 'Failed'); } finally { setBusy(null); }
  };
  if (!data) return <div style={{ color: t.muted }}>Loading…</div>;
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 4px' }}>Access</h2>
      <div style={{ fontSize: 12.5, color: t.muted, marginBottom: 16 }}>Grant Attendance access per person. Nobody has access by default.</div>
      <div style={{ ...panel, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ color: t.muted, textAlign: 'left', borderBottom: `1px solid ${t.border}` }}>{['User', 'Level', 'Group scope', 'Export', 'Leave'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {data.users.map(u => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                <td style={{ ...td, fontWeight: 600 }}>{u.full_name}{u.is_super && <span style={{ fontSize: 10, color: t.brand, marginLeft: 6 }}>Super</span>}</td>
                <td style={td}>{u.is_super ? <span style={{ color: t.green, fontWeight: 700, fontSize: 12 }}>Administrator</span> :
                  <select disabled={busy === u.id} value={u.level} onChange={e => update(u, { level: e.target.value })} style={{ ...inputCss, width: 'auto', padding: '5px 8px', fontSize: 12 }}>{['none', 'viewer', 'coordinator', 'administrator'].map(l => <option key={l} value={l}>{l}</option>)}</select>}</td>
                <td style={td}>{!u.is_super && u.level !== 'none' && u.level !== 'administrator' ?
                  <select disabled={busy === u.id} value={u.group_id || ''} onChange={e => update(u, { group_id: e.target.value ? +e.target.value : null })} style={{ ...inputCss, width: 'auto', padding: '5px 8px', fontSize: 12 }}><option value="">All groups</option>{data.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
                  : <span style={{ color: t.muted }}>—</span>}</td>
                <td style={td}>{!u.is_super && u.level !== 'none' ?
                  <input type="checkbox" disabled={busy === u.id} checked={!!u.can_export} onChange={e => update(u, { can_export: e.target.checked })} />
                  : <span style={{ color: t.muted }}>{u.is_super ? 'Yes' : '—'}</span>}</td>
                <td style={td}>{!u.is_super && (u.level === 'viewer') ?
                  <input type="checkbox" disabled={busy === u.id} checked={!!u.can_manage_leave} onChange={e => update(u, { can_manage_leave: e.target.checked })} title="Let this viewer record leave and reasons" />
                  : <span style={{ color: t.muted }}>{u.is_super || u.level === 'coordinator' || u.level === 'administrator' ? 'Yes' : '—'}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActivityView() {
  const [rows, setRows] = useState([]);
  const [level, setLevel] = useState('all');
  const [q, setQ] = useState('');
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (level !== 'all') p.set('level', level); if (q) p.set('q', q);
    attendanceApi.get('/activity?' + p.toString()).then(setRows).catch(() => setRows([]));
  }, [level, q]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);
  const colour = (l) => l === 'error' ? t.red : l === 'warn' ? t.amber : t.muted;
  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 12px' }}>History</h2>
      <div style={{ ...panel, padding: 12, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 2, minWidth: 200 }}><label style={labelCss}>Search</label><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search history…" style={inputCss} /></div>
        <div style={{ flex: 1, minWidth: 150 }}><label style={labelCss}>Type</label>
          <select value={level} style={inputCss} onChange={e => setLevel(e.target.value)}>
            <option value="all">All events</option><option value="info">Info</option><option value="warn">Warnings</option><option value="error">Errors</option>
          </select>
        </div>
      </div>
      <div style={{ ...panel, padding: 8 }}>
        {rows.length === 0 && <div style={{ color: t.muted, padding: 16 }}>No matching activity.</div>}
        {rows.map(r => (
          <div key={r.id} style={{ padding: '8px 12px', borderBottom: `1px solid ${t.border}`, fontSize: 13, display: 'flex', gap: 12, alignItems: 'baseline' }}>
            <span style={{ color: t.muted, fontSize: 11, minWidth: 150 }}>{new Date(r.at).toLocaleString()}</span>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: colour(r.level), minWidth: 46 }}>{r.level}</span>
            <span>{r.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
